import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Disclaimer } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Search, Video, CalendarDays, Loader2, Compass, Clock, MessageCircle, Package, Eye, Star, Navigation } from "lucide-react";

const isOnline = (m) => (m || "").toLowerCase().includes("online");
const isPresencial = (m) => (m || "").toLowerCase().includes("presencial");
const MapEmbed = ({ address }) => (
  <iframe title="Mapa" loading="lazy" data-testid="directory-map" className="w-full h-40 rounded-xl border border-border mt-2"
    src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`} />
);

const haversine = (a, b) => {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const Stars = ({ value, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} de 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} style={{ width: size, height: size }} className={n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300 dark:text-slate-600"} />
    ))}
  </span>
);

export default function TherapistDirectory() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("all");
  const [modality, setModality] = useState("all");
  const [selecting, setSelecting] = useState(null);
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    api.get("/therapists/directory").then(({ data }) => setList(data)).catch(() => setList([]));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}, { timeout: 8000 });
    }
  }, []);

  const openProfile = (t) => {
    setProfile(t); setReviews([]);
    api.get(`/reviews?therapist_id=${t.id}`).then(({ data }) => setReviews(data)).catch(() => {});
  };

  const choose = async (t) => {
    setSelecting(t.id);
    try {
      await api.post("/select-therapist", { therapist_id: t.id });
      toast.success(`Você escolheu ${t.name}. Agende sua consulta!`);
      nav("/app/consultas");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSelecting(null);
    }
  };

  const distanceLabel = (t) => {
    if (!coords || !t.profile?.geo || !isPresencial(t.profile?.modality)) return null;
    const km = haversine(coords, t.profile.geo);
    return km < 1 ? "menos de 1 km de você" : `~${Math.round(km)} km de você`;
  };

  if (list === null) return <div className="h-40 animate-pulse bg-secondary rounded-2xl" />;

  const allSpecialties = [...new Set(list.flatMap((t) => (t.profile?.specialties || "").split(",").map((s) => s.trim()).filter(Boolean)))].sort();
  const term = q.trim().toLowerCase();
  const filtered = list.filter((t) => {
    if (term && !((t.region || "").toLowerCase().includes(term) || (t.name || "").toLowerCase().includes(term) || (t.profile?.specialties || "").toLowerCase().includes(term))) return false;
    if (specialty !== "all" && !(t.profile?.specialties || "").toLowerCase().includes(specialty.toLowerCase())) return false;
    if (modality === "online" && !isOnline(t.profile?.modality)) return false;
    if (modality === "presencial" && !isPresencial(t.profile?.modality)) return false;
    return true;
  });

  const groups = {};
  filtered.forEach((t) => { const r = t.region || "Outros"; (groups[r] = groups[r] || []).push(t); });
  const regions = Object.keys(groups).sort();

  return (
    <div data-testid="therapist-directory">
      <PageHeader title="Encontrar Profissional" subtitle="Escolha um hipnoterapeuta por região. Profissionais que atendem online também aparecem aqui." />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busque por cidade, nome ou especialidade" className="pl-9 rounded-full" data-testid="directory-search" />
        </div>
        <Select value={specialty} onValueChange={setSpecialty}>
          <SelectTrigger className="w-full sm:w-52 rounded-full" data-testid="filter-specialty"><SelectValue placeholder="Especialidade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas especialidades</SelectItem>
            {allSpecialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={modality} onValueChange={setModality}>
          <SelectTrigger className="w-full sm:w-44 rounded-full" data-testid="filter-modality"><SelectValue placeholder="Modalidade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas modalidades</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="presencial">Presencial</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <Compass className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhum profissional encontrado para estes filtros.</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {regions.map((region) => (
            <div key={region} data-testid={`region-${region}`}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-primary" />
                <h3 className="font-heading font-medium">{region}</h3>
                <span className="text-xs text-muted-foreground">({groups[region].length})</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {groups[region].map((t) => {
                  const dist = distanceLabel(t);
                  return (
                    <Card key={t.id} className="p-5 flex flex-col" data-testid={`therapist-${t.id}`}>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-14 h-14"><AvatarImage src={t.profile?.photo} /><AvatarFallback className="bg-primary/10 text-primary text-lg">{t.name[0]}</AvatarFallback></Avatar>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{t.name}</div>
                          {t.profile?.modality && <div className="text-xs text-muted-foreground flex items-center gap-1">{isOnline(t.profile.modality) && <Video className="w-3 h-3" />}{t.profile.modality}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2" data-testid={`rating-${t.id}`}>
                        <Stars value={t.rating?.avg || 0} />
                        <span className="text-xs text-muted-foreground">{t.rating?.count ? `${t.rating.avg} (${t.rating.count})` : "Sem avaliações"}</span>
                      </div>
                      {t.profile?.specialties && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {t.profile.specialties.split(",").slice(0, 3).map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s.trim()}</Badge>)}
                        </div>
                      )}
                      {t.profile?.bio && <p className="text-sm text-muted-foreground line-clamp-2 mt-2 flex-1">{t.profile.bio}</p>}
                      {t.profile?.address && <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />{t.profile.address}</p>}
                      {dist && <p className="text-xs text-accent font-medium mt-1 flex items-center gap-1" data-testid={`distance-${t.id}`}><Navigation className="w-3.5 h-3.5" />{dist}</p>}
                      {t.values?.first_consult ? <p className="text-sm font-medium mt-2">1ª consulta: R$ {Number(t.values.first_consult).toFixed(2)}</p> : null}
                      <div className="flex gap-2 mt-4">
                        <Button variant="outline" className="rounded-full flex-1" onClick={() => openProfile(t)} data-testid={`view-profile-${t.id}`}><Eye className="w-4 h-4 mr-1" />Ver perfil</Button>
                        <Button className="rounded-full flex-1" onClick={() => choose(t)} disabled={selecting === t.id} data-testid={`choose-therapist-${t.id}`}>
                          {selecting === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarDays className="w-4 h-4 mr-1" />Agendar</>}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
          {profile && (
            <>
              <DialogHeader><DialogTitle>Perfil do profissional</DialogTitle></DialogHeader>
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16"><AvatarImage src={profile.profile?.photo} /><AvatarFallback className="bg-primary/10 text-primary text-xl">{profile.name[0]}</AvatarFallback></Avatar>
                <div>
                  <div className="font-heading text-lg font-medium">{profile.name}</div>
                  {profile.profile?.modality && <div className="text-sm text-muted-foreground flex items-center gap-1">{isOnline(profile.profile.modality) && <Video className="w-3.5 h-3.5" />}{profile.profile.modality}</div>}
                  <div className="flex items-center gap-2 mt-1"><Stars value={profile.rating?.avg || 0} /><span className="text-xs text-muted-foreground">{profile.rating?.count ? `${profile.rating.avg} · ${profile.rating.count} avaliação(ões)` : "Sem avaliações"}</span></div>
                </div>
              </div>
              {profile.profile?.specialties && (
                <div className="flex flex-wrap gap-1.5">
                  {profile.profile.specialties.split(",").map((s, i) => <Badge key={i} variant="secondary">{s.trim()}</Badge>)}
                </div>
              )}
              {profile.profile?.bio && <p className="text-sm text-muted-foreground">{profile.profile.bio}</p>}
              {profile.profile?.schedule && <p className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-primary" />{profile.profile.schedule}</p>}

              {profile.values && (Object.keys(profile.values).length > 0) && (
                <div className="rounded-xl bg-secondary/50 p-3">
                  <div className="text-sm font-medium mb-2">Valores</div>
                  <div className="grid grid-cols-2 gap-1.5 text-sm">
                    {profile.values.first_consult ? <div className="flex justify-between"><span className="text-muted-foreground">1ª consulta</span><span>R$ {Number(profile.values.first_consult).toFixed(2)}</span></div> : null}
                    {profile.values.followup ? <div className="flex justify-between"><span className="text-muted-foreground">Retorno</span><span>R$ {Number(profile.values.followup).toFixed(2)}</span></div> : null}
                    {profile.values.online ? <div className="flex justify-between"><span className="text-muted-foreground">Online</span><span>R$ {Number(profile.values.online).toFixed(2)}</span></div> : null}
                    {profile.values.other ? <div className="flex justify-between"><span className="text-muted-foreground">Outros</span><span>R$ {Number(profile.values.other).toFixed(2)}</span></div> : null}
                  </div>
                </div>
              )}
              {profile.values?.packages?.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1.5 flex items-center gap-1.5"><Package className="w-4 h-4 text-accent" />Pacotes</div>
                  <div className="space-y-1.5">
                    {profile.values.packages.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm rounded-lg border border-border px-3 py-2"><span>{p.name}{p.sessions ? ` · ${p.sessions} sessões` : ""}</span><span className="font-medium">R$ {Number(p.price).toFixed(2)}</span></div>
                    ))}
                  </div>
                </div>
              )}
              {profile.profile?.address && (
                <div>
                  <p className="text-sm flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" />{profile.profile.address}</p>
                  <MapEmbed address={profile.profile.address} />
                </div>
              )}

              <div data-testid="reviews-section">
                <div className="text-sm font-medium mb-1.5 flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-400" />Avaliações de clientes</div>
                {reviews.length === 0 ? <p className="text-sm text-muted-foreground">Ainda sem avaliações.</p> : (
                  <div className="space-y-2">
                    {reviews.slice(0, 5).map((rv) => (
                      <div key={rv.id} className="rounded-lg border border-border p-3">
                        <div className="flex items-center justify-between"><span className="text-sm font-medium">{rv.client_name}</span><Stars value={rv.rating} size={12} /></div>
                        {rv.comment && <p className="text-sm text-muted-foreground mt-1">{rv.comment}</p>}
                        {rv.reply && <div className="mt-2 pl-3 border-l-2 border-primary/40"><p className="text-xs font-medium text-primary">Resposta do profissional</p><p className="text-sm text-muted-foreground">{rv.reply}</p></div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                {profile.profile?.whatsapp && <a href={`https://wa.me/${profile.profile.whatsapp}`} target="_blank" rel="noreferrer" className="flex-1"><Button variant="outline" className="rounded-full w-full"><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</Button></a>}
                <Button className="rounded-full flex-1" onClick={() => choose(profile)} data-testid="profile-choose-btn"><CalendarDays className="w-4 h-4 mr-1" />Escolher e agendar</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Disclaimer />
    </div>
  );
}
